import type { LineStringGeometry, PolygonGeometry } from '@trafiku/contracts';
import type { Feature } from 'geojson';
import type {
  GeoJSONSource,
  MapLayerMouseEvent,
  MapLayerTouchEvent,
  MapMouseEvent,
  MapTouchEvent,
} from 'maplibre-gl';
import { useEffect, useId, useRef } from 'react';
import { fc } from './layers';
import { useMap } from './map-view';

type Shape = LineStringGeometry | PolygonGeometry;
type Pt = [number, number];

/** The editable points: a line's coordinates, or a polygon's outer ring without the closing point. */
function pointsOf(g: Shape): Pt[] {
  if (g.type === 'LineString') return g.coordinates.map((c) => [c[0]!, c[1]!]);
  const ring = (g.coordinates[0] ?? []).map((c) => [c[0]!, c[1]!] as Pt);
  const [first, last] = [ring[0], ring.at(-1)];
  return first && last && first[0] === last[0] && first[1] === last[1] ? ring.slice(0, -1) : ring;
}

const round = (p: Pt): Pt => [Math.round(p[0] * 1e7) / 1e7, Math.round(p[1] * 1e7) / 1e7];

/** Rebuilds the geometry from edited points (polygon holes are kept as they were). */
function withPoints(g: Shape, pts: Pt[]): Shape {
  const r = pts.map(round);
  if (g.type === 'LineString') return { type: 'LineString', coordinates: r } as Shape;
  return { type: 'Polygon', coordinates: [[...r, r[0]!], ...g.coordinates.slice(1)] } as Shape;
}

/**
 * A line or polygon whose points can be dragged directly (no selection step): drag a point to move
 * it, drag a midpoint to insert one, right-click a point to delete it. Pressing on a point stops the
 * map from panning for that gesture, so even quick drags move the point.
 */
export function VertexEditor({
  geometry,
  color,
  editable,
  onChange,
}: {
  geometry: Shape;
  color: string;
  editable: boolean;
  onChange: (g: Shape) => void;
}) {
  const map = useMap();
  const id = `edit-${useId().replace(/:/g, '')}`;
  const shape = useRef(geometry);
  const pts = useRef<Pt[]>(pointsOf(geometry));
  const drag = useRef<number | null>(null);
  const editableRef = useRef(editable);
  editableRef.current = editable;
  const change = useRef(onChange);
  change.current = onChange;
  const isPolygon = geometry.type === 'Polygon';

  const render = () => {
    if (!map?.getSource(`${id}-shape`)) return;
    const p = pts.current;
    const g = withPoints(shape.current, p);
    (map.getSource(`${id}-shape`) as GeoJSONSource).setData(
      fc([{ type: 'Feature', geometry: g, properties: {} }]),
    );
    const show = editableRef.current;
    (map.getSource(`${id}-pts`) as GeoJSONSource).setData(
      fc(
        show
          ? p.map(
              (c, i): Feature => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: c },
                properties: { i },
              }),
            )
          : [],
      ),
    );
    const mids: Feature[] = [];
    if (show) {
      const n = isPolygon ? p.length : p.length - 1;
      for (let i = 0; i < n; i++) {
        const [a, b] = [p[i]!, p[(i + 1) % p.length]!];
        mids.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] },
          properties: { i },
        });
      }
    }
    (map.getSource(`${id}-mid`) as GeoJSONSource).setData(fc(mids));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: sources and handlers are set up once per map
  useEffect(() => {
    if (!map) return;
    const empty = fc([]);
    map.addSource(`${id}-shape`, { type: 'geojson', data: empty });
    map.addSource(`${id}-pts`, { type: 'geojson', data: empty });
    map.addSource(`${id}-mid`, { type: 'geojson', data: empty });
    if (isPolygon)
      map.addLayer({
        id: `${id}-fill`,
        type: 'fill',
        source: `${id}-shape`,
        paint: { 'fill-color': color, 'fill-opacity': 0.2 },
      });
    map.addLayer({
      id: `${id}-line`,
      type: 'line',
      source: `${id}-shape`,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': color, 'line-width': isPolygon ? 2.5 : 4 },
    });
    map.addLayer({
      id: `${id}-mid`,
      type: 'circle',
      source: `${id}-mid`,
      paint: {
        'circle-radius': 4,
        'circle-color': '#ffffff',
        'circle-opacity': 0.85,
        'circle-stroke-color': color,
        'circle-stroke-width': 1.5,
      },
    });
    map.addLayer({
      id: `${id}-pts`,
      type: 'circle',
      source: `${id}-pts`,
      paint: {
        'circle-radius': 6,
        'circle-color': color,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
      },
    });

    const canvas = map.getCanvas();
    const startDrag = (index: number) => {
      drag.current = index;
      canvas.style.cursor = 'grabbing';
    };
    const onPointDown = (e: MapLayerMouseEvent | MapLayerTouchEvent) => {
      if (!editableRef.current || ('button' in e.originalEvent && e.originalEvent.button !== 0)) return;
      const i = Number(e.features?.[0]?.properties?.i);
      if (!Number.isInteger(i)) return;
      e.preventDefault(); // no map panning for this gesture
      startDrag(i);
    };
    const onMidDown = (e: MapLayerMouseEvent | MapLayerTouchEvent) => {
      if (!editableRef.current || ('button' in e.originalEvent && e.originalEvent.button !== 0)) return;
      const i = Number(e.features?.[0]?.properties?.i);
      if (!Number.isInteger(i)) return;
      e.preventDefault();
      pts.current = [
        ...pts.current.slice(0, i + 1),
        [e.lngLat.lng, e.lngLat.lat],
        ...pts.current.slice(i + 1),
      ];
      startDrag(i + 1);
      render();
    };
    const onMove = (e: MapMouseEvent | MapTouchEvent) => {
      if (drag.current === null) return;
      pts.current[drag.current] = [e.lngLat.lng, e.lngLat.lat];
      render();
    };
    const onUp = () => {
      if (drag.current === null) return;
      drag.current = null;
      canvas.style.cursor = '';
      shape.current = withPoints(shape.current, pts.current);
      change.current(shape.current);
    };
    const onDelete = (e: MapLayerMouseEvent) => {
      if (!editableRef.current) return;
      e.preventDefault();
      const i = Number(e.features?.[0]?.properties?.i);
      if (pts.current.length <= (isPolygon ? 3 : 2) || !Number.isInteger(i)) return;
      pts.current = pts.current.filter((_, j) => j !== i);
      shape.current = withPoints(shape.current, pts.current);
      render();
      change.current(shape.current);
    };
    const hover = () => {
      if (editableRef.current && drag.current === null) canvas.style.cursor = 'grab';
    };
    const unhover = () => {
      if (drag.current === null) canvas.style.cursor = '';
    };

    map.on('mousedown', `${id}-pts`, onPointDown);
    map.on('touchstart', `${id}-pts`, onPointDown);
    map.on('mousedown', `${id}-mid`, onMidDown);
    map.on('touchstart', `${id}-mid`, onMidDown);
    map.on('contextmenu', `${id}-pts`, onDelete);
    map.on('mouseenter', `${id}-pts`, hover);
    map.on('mouseenter', `${id}-mid`, hover);
    map.on('mouseleave', `${id}-pts`, unhover);
    map.on('mouseleave', `${id}-mid`, unhover);
    map.on('mousemove', onMove);
    map.on('touchmove', onMove);
    map.on('mouseup', onUp);
    map.on('touchend', onUp);
    render();

    return () => {
      map.off('mousedown', `${id}-pts`, onPointDown);
      map.off('touchstart', `${id}-pts`, onPointDown);
      map.off('mousedown', `${id}-mid`, onMidDown);
      map.off('touchstart', `${id}-mid`, onMidDown);
      map.off('contextmenu', `${id}-pts`, onDelete);
      map.off('mouseenter', `${id}-pts`, hover);
      map.off('mouseenter', `${id}-mid`, hover);
      map.off('mouseleave', `${id}-pts`, unhover);
      map.off('mouseleave', `${id}-mid`, unhover);
      map.off('mousemove', onMove);
      map.off('touchmove', onMove);
      map.off('mouseup', onUp);
      map.off('touchend', onUp);
      if (!map.getStyle()) return;
      for (const l of ['pts', 'mid', 'line', 'fill'])
        if (map.getLayer(`${id}-${l}`)) map.removeLayer(`${id}-${l}`);
      for (const s of ['pts', 'mid', 'shape'])
        if (map.getSource(`${id}-${s}`)) map.removeSource(`${id}-${s}`);
    };
  }, [map, id, isPolygon]);

  // A new geometry from outside (loaded record, redraw) replaces the working copy, except mid-drag.
  // biome-ignore lint/correctness/useExhaustiveDependencies: render reads refs
  useEffect(() => {
    if (drag.current !== null) return;
    shape.current = geometry;
    pts.current = pointsOf(geometry);
    render();
  }, [geometry, editable, map]);

  useEffect(() => {
    if (!map?.getLayer(`${id}-line`)) return;
    map.setPaintProperty(`${id}-line`, 'line-color', color);
    map.setPaintProperty(`${id}-pts`, 'circle-color', color);
    map.setPaintProperty(`${id}-mid`, 'circle-stroke-color', color);
    if (map.getLayer(`${id}-fill`)) map.setPaintProperty(`${id}-fill`, 'fill-color', color);
  }, [map, id, color]);

  return null;
}
